// === Onboarding + Home Overview ===
//
// Step 1 doubles as the "Home" screen. Flow on load:
//   - First-time (no profile): Welcome splash ("Get Started") → Settings → app
//   - Returning, fresh start:  Home screen ("Welcome back" + herd overview,
//     progress, install hint, "View my calendar")
//   - Returning, same session (reload / pull-to-refresh): straight to the app
// The Home screen is also reachable any time via the header home icon / logo.

import store, { PROFILE_KEY, SEASONS_KEY } from '../state/store.js';
import { getElements } from '../utils/dom.js';
import { removeItem } from '../services/storage-service.js';
import {
  loadProfile,
  applyPendingProfileToUI,
  toggleOnboarding,
  setPendingChoice,
  saveProfileAndContinue,
  applyDefaultSeasons
} from '../services/profile-service.js';
import { renderCards } from './card-renderer.js';
import { canInstall, triggerInstall } from './pwa-install.js';
import { filterAdvisories } from '../services/filter-service.js';
import { isDone } from '../services/completion-service.js';
import { SHORT_MONTHS, MONTH_NAMES } from '../utils/date.js';
import { showToast } from '../utils/toast.js';
import { pushScreen, back, hasScreens } from '../utils/nav.js';

const SESSION_ENTERED_KEY = 'farm_session_entered';

const SEASON_LABELS = {
  calving: 'Calving', turnout: 'Turnout', housing: 'Housing',
  breeding: 'Breeding', dryingOff: 'Drying off'
};

function hasCompleteProfile() {
  return !!(store.farmProfile.herdType && store.farmSeasons.calving);
}

function sessionEntered() {
  try { return sessionStorage.getItem(SESSION_ENTERED_KEY) === '1'; }
  catch { return false; }
}

function markSessionEntered() {
  try { sessionStorage.setItem(SESSION_ENTERED_KEY, '1'); }
  catch { /* ignore */ }
}

// Fill the Home overview panel: herd type, season dates, this month's
// progress, and the install hint. Safe to call before data has loaded
// (progress just shows 0 / 0 until the seed is in).
export function renderHome() {
  const els = getElements();
  if (!els.homePanel) return;

  els.homeHerdType.textContent = store.farmProfile.herdType || '—';

  const s = store.farmSeasons || {};
  els.homeSeasons.innerHTML = Object.keys(SEASON_LABELS)
    .filter(k => s[k])
    .map(k => `<li><span class="home-season-name">${SEASON_LABELS[k]}</span><span class="home-season-month">${SHORT_MONTHS[s[k] - 1]}</span></li>`)
    .join('');

  const items = filterAdvisories();
  const total = items.length;
  const done = items.filter(i => isDone(i.id)).length;
  els.homeProgressLabel.textContent = MONTH_NAMES[store.currentMonth] || '';
  els.homeProgressCount.textContent = `${done} / ${total} done`;
  els.homeProgressFill.style.width = (total ? Math.round((done / total) * 100) : 0) + '%';

  if (els.homeInstall) els.homeInstall.hidden = !canInstall();
}

// Configure Step 1 for a returning user (Home) vs a first-timer (splash).
function applySplashMode(els, returning) {
  if (returning) {
    const herd = store.farmProfile.herdType || '';
    els.splashTitle.innerHTML = 'Welcome back';
    els.splashSubtitle.textContent = herd
      ? `Your ${herd} herd calendar is ready.`
      : 'Your herd calendar is ready.';
    els.btnGetStarted.textContent = 'View my calendar';
    if (els.splashResetHint) els.splashResetHint.hidden = true;
    if (els.homePanel) els.homePanel.hidden = false;
    if (els.homeTextlinks) els.homeTextlinks.hidden = false;
    renderHome();
  } else {
    els.splashTitle.innerHTML = 'Herd Health<br>Advisory Calendar';
    els.splashSubtitle.textContent = 'Your personalised monthly guide to better herd health — powered by Animal Health Ireland.';
    els.btnGetStarted.textContent = 'Get Started';
    if (els.splashResetHint) els.splashResetHint.hidden = false;
    if (els.homePanel) els.homePanel.hidden = true;
    if (els.homeTextlinks) els.homeTextlinks.hidden = true;
  }
}

// Show the Home / start screen — the dismiss target when Back is pressed from
// the calendar. Mirrors the header home-icon action.
function showHomeScreen() {
  const els = getElements();
  applySplashMode(els, true);
  els.onboardStep1.style.display = 'flex';
  els.onboardStep2.style.display = 'none';
  toggleOnboarding(true, 1);
}

export function initOnboarding() {
  const els = getElements();

  loadProfile();
  applyPendingProfileToUI();

  const returning = hasCompleteProfile();

  if (returning && sessionEntered()) {
    // Reload / pull-to-refresh within the same session → stay in the app.
    toggleOnboarding(false);
    if (!hasScreens()) pushScreen(showHomeScreen);
  } else {
    applySplashMode(els, returning);
    toggleOnboarding(true, 1);
  }

  // Step 1 button: returning users skip Settings and enter the app directly;
  // first-timers go on to Settings (Step 2).
  els.btnGetStarted.addEventListener('click', () => {
    if (hasCompleteProfile()) {
      enterApp();
    } else {
      els.onboardStep1.style.display = 'none';
      els.onboardStep2.style.display = 'flex';
      els.onboardStep2.querySelector('.config-card').style.animation = 'cardSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)';
    }
  });

  // Home → install + edit-setup actions
  if (els.btnHomeInstall) els.btnHomeInstall.addEventListener('click', () => triggerInstall());
  if (els.btnHomeEditSetup) els.btnHomeEditSetup.addEventListener('click', () => {
    store.pendingProfile = { ...store.farmProfile };
    store.pendingSeasons = { ...store.farmSeasons };
    applyPendingProfileToUI();
    els.onboardStep1.style.display = 'none';
    els.onboardStep2.style.display = 'flex';
  });

  // Home → "Start over": wipe the saved farm setup and return to the true
  // first-run welcome screen (user taps Get Started and sets up again). A full
  // reload guarantees a clean first-run state. Completed-task progress is kept.
  if (els.btnHomeDefaults) els.btnHomeDefaults.addEventListener('click', () => {
    removeItem(PROFILE_KEY);
    removeItem(SEASONS_KEY);
    try { sessionStorage.removeItem(SESSION_ENTERED_KEY); } catch { /* ignore */ }
    window.location.reload();
  });

  // Step 2 → Main App
  els.startPlanning.addEventListener('click', () => {
    if (saveProfileAndContinue(renderCards)) {
      markSessionEntered();
      if (!hasScreens()) pushScreen(showHomeScreen);
    }
  });

  // Edit Profile (from header gear) → go directly to Step 2
  els.btnEditProfile.addEventListener('click', () => {
    store.pendingProfile = { ...store.farmProfile };
    store.pendingSeasons = { ...store.farmSeasons };
    applyPendingProfileToUI();
    toggleOnboarding(true, 2);
  });

  els.herdButtons.forEach(btn =>
    btn.addEventListener('click', () => setPendingChoice('herdType', btn.dataset.herd))
  );

  // Season select change handlers
  els.seasonSelects.forEach(sel =>
    sel.addEventListener('change', () => {
      const key = sel.dataset.season;
      store.pendingSeasons[key] = parseInt(sel.value) || null;
    })
  );

  // Header home icon / logo → open the Home overview (only meaningful once a
  // profile exists; first-timers have nothing to show yet).
  window.app.openHome = () => {
    if (!hasCompleteProfile()) return;
    // Pop the calendar off the back-stack → showHomeScreen (keeps Back in sync).
    if (hasScreens()) back(); else showHomeScreen();
  };
}

// Returning user enters the calendar straight from the Home / welcome screen.
function enterApp() {
  toggleOnboarding(false);
  renderCards();
  markSessionEntered();
  if (!hasScreens()) pushScreen(showHomeScreen);
}
